import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  model,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { produce } from 'immer';
import { generateId } from '@lc/util';
import { ControlValueAccessor } from '@angular/forms';
import { provideValueAccessor } from '../util';
import { BspkFormField } from '../form-field';
import { BspkChip } from '../chip';

export type MultiSelectSize = 'medium' | 'large';

@Component({
  selector: 'bspk-multi-select',
  imports: [BspkFormField, BspkChip],
  templateUrl: './multi-select.component.html',
  styleUrl: './multi-select.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'data-bspk': 'multi-select',
    role: 'listbox',
    '[attr.data-size]': 'size()',
    '[attr.data-readonly]': 'readonly() || null',
    '[attr.aria-disabled]': 'disabled() || null',
    '[attr.aria-invalid]': 'invalid() || null',
    '[attr.aria-label]': 'label()',
  },
  providers: [provideValueAccessor(BspkMultiSelect)],
})
export class BspkMultiSelect implements ControlValueAccessor {
  readonly values = model<string[]>([]);
  readonly label = input.required<string>();
  readonly controlId = input(generateId('multi-select'));
  readonly helperText = input<string>();
  readonly errorMessage = input<string>();
  readonly size = input<MultiSelectSize>('medium');
  readonly readonly = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly invalid = input(false, { transform: booleanAttribute });
  readonly blur = output<FocusEvent>();

  readonly inputValue = signal('');
  readonly input = viewChild.required<HTMLInputElement, ElementRef<HTMLInputElement>>(
    HTMLInputElement,
    { read: ElementRef },
  );

  remove(index: number) {
    this.values.update(
      produce((draft) => {
        draft.splice(index, 1);
      }),
    );
  }

  addValue() {
    if (this.inputValue()) {
      this.values.update(
        produce((draft) => {
          draft.push(this.inputValue());
        }),
      );
      this.inputValue.set('');
    }
  }

  onInput(event: Event) {
    const target = event.target as HTMLInputElement;
    const { value } = target;
    if (value.endsWith(',')) {
      this.addValue();
    } else {
      this.inputValue.set(value);
    }
  }

  registerOnChange(fn: any): void {
    this.values.subscribe(fn);
  }

  registerOnTouched(fn: any): void {
  }

  setDisabledState(isDisabled: boolean): void {
  }

  writeValue(obj: any): void {
    if (Array.isArray(obj)) {
      this.values.set(obj);
    }
  }
}
